/**
 * Adresse der Shell (B4, PF-G): Modul und Seite stehen in der URL, damit Neuladen,
 * Deep-Links und die Zurück-Taste an derselben Stelle landen.
 *
 *   /                         Startseite
 *   /<route>                  Modul, seine erste Seite
 *   /<route>/<seite>          Modulseite (`path` aus der Sidebar des Moduls)
 *   /<route>/@modul/<info>    Shell-eigene Modulseite (Plan, Doku, Daten …)
 *   /<route>#<ziel>           alte Deep-Links der Ammon-Abfrage, z. B. #bestellung.BL1
 *
 * Ohne Importe, damit sie sich ohne Bundler prüfen lässt.
 */

export interface AdressModul {
  id: string;
  /** z. B. "/einkauf" */
  route: string;
}

export interface AdressZiel {
  modulId: string;
  /** Unterpfad für `onNavigate` des Moduls; fehlt = erste Seite. */
  pfad?: string;
}

/** Liest Modul und Unterpfad aus Pfad und Hash; null = Startseite oder kein freigegebenes Modul. */
export function adresseLesen(
  pfadname: string,
  hash: string,
  module: readonly AdressModul[],
): AdressZiel | null {
  const pfad = '/' + pfadname.replace(/^\/+/, '').replace(/\/+$/, '');
  if (pfad === '/') return null;
  // Die längste passende Route gewinnt (z. B. /einkauf vor /ein).
  const modul = [...module]
    .sort((a, b) => b.route.length - a.route.length)
    .find((m) => pfad === m.route || pfad.startsWith(`${m.route}/`));
  if (!modul) return null;
  let rest = pfad.slice(modul.route.length).replace(/^\/+/, '');
  try {
    rest = decodeURIComponent(rest);
  } catch {
    // Eine kaputte Kodierung öffnet das Modul ohne Unterpfad.
    rest = '';
  }
  const anker = hash.replace(/^#/, '');
  const unterpfad = rest || anker;
  return unterpfad ? { modulId: modul.id, pfad: unterpfad } : { modulId: modul.id };
}

/** Die Adresse für den aktuellen Stand der Shell. */
export function adresseFuer(modul: AdressModul | null, seite?: string | null): string {
  if (!modul) return '/';
  // Leere Abschnitte sowie `.`/`..` verwerfen: der Browser löste sie auf und die
  // Adresse führte aus dem Modul hinaus (/einkauf/../bridge).
  const teile = (seite ?? '').split('/').filter((teil) => teil && teil !== '.' && teil !== '..');
  // „@" bleibt lesbar (/einkauf/@modul/plan), alles andere wird je Abschnitt kodiert.
  const kodiert = teile.map((teil) => encodeURIComponent(teil).replace(/%40/g, '@')).join('/');
  return kodiert ? `${modul.route}/${kodiert}` : modul.route;
}

/** Was mit der Browser-Adresse geschieht, wenn sich der Stand der Shell ändert. */
export type AdressSchritt = 'nichts' | 'ersetzen' | 'neu' | 'warten';

export interface AdressLage {
  /** Soll-Adresse aus `adresseFuer` (nie mit Hash). */
  adresse: string;
  /** `location.pathname` des Browsers. */
  pfadname: string;
  /** Erste Angleichung nach dem Laden. */
  ersterLauf: boolean;
  /** Nach „Zurück"/„Vor" erwartete Adresse; null = keine Verlaufsbewegung offen. */
  erwartet: string | null;
  /** Route des offenen Moduls, z. B. "/einkauf"; null = Startseite. */
  modulRoute: string | null;
}

/**
 * Entscheidet, ob die Adresse bleibt, ersetzt, als neuer Verlaufseintrag geschrieben
 * wird oder (nach „Zurück") auf den passenden Stand der Shell wartet.
 */
export function adressSchritt(lage: AdressLage): AdressSchritt {
  const { adresse, pfadname, ersterLauf, erwartet, modulRoute } = lage;
  // Nach „Zurück" steht die Adresse schon; Zwischenstände (noch die alte Seite)
  // nicht zurückschreiben, bis die Shell den erwarteten Stand erreicht hat.
  if (erwartet !== null && adresse !== erwartet) return 'warten';
  // Der Hash gehört dem Modul (Anker) oder ist ein alter Deep-Link — nur der Pfad zählt.
  if (adresse === pfadname) return 'nichts';
  // Erster Lauf: der Deep-Link wird zur Seite. Nach „Zurück" nur angleichen (z. B. #-Link).
  if (ersterLauf || erwartet !== null) return 'ersetzen';
  // Meldet ein frisch geöffnetes Modul seine erste Seite (/einkauf → /einkauf/artikel),
  // ist das kein neuer Schritt im Verlauf.
  if (modulRoute !== null && pfadname === modulRoute && adresse.startsWith(`${modulRoute}/`)) {
    return 'ersetzen';
  }
  return 'neu';
}
